import { Layout } from '@/components/Layout';
import { PlanSection, ScheduleChart } from '@/components/PlanSection';
import { developmentSchedule, projectPlanData } from '@/data/projectPlan';
import { formatDate } from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { motion } from 'framer-motion';
import { Calendar, CheckCircle2, Clock, Package } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

export default function Schedule() {
  const { phases, milestones, techStack } = developmentSchedule;
  const { startDate, endDate, totalWeeks } = projectPlanData;

  const chartData = phases.map(phase => ({
    phase: phase.name,
    startWeek: phase.startWeek,
    endWeek: phase.endWeek,
    tasks: phase.tasks,
  }));

  return (
    <Layout>
      <div className="relative">
        <div className="absolute inset-0 z-0 opacity-30">
          <img
            src={IMAGES.TEAM_WORK_2}
            alt="팀 협업"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />

        <div className="relative z-10 container mx-auto px-4 py-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-4">
              <Calendar className="w-8 h-8 text-primary" />
              <h1 className="text-4xl font-bold">개발 일정</h1>
            </div>
            <p className="text-lg text-muted-foreground">
              프로젝트 전체 일정표와 단계별 개발 계획을 확인하세요
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mb-12"
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />
                  프로젝트 기간
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">시작일</p>
                    <p className="text-lg font-semibold">{formatDate(startDate)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">종료일</p>
                    <p className="text-lg font-semibold">{formatDate(endDate)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">총 기간</p>
                    <p className="text-lg font-semibold">{totalWeeks}주</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mb-12"
          >
            <PlanSection title="개발 단계별 일정">
              <ScheduleChart data={chartData} />
              <div className="mt-8 space-y-6">
                {phases.map((phase, index) => (
                  <motion.div
                    key={phase.id}
                    initial={{ opacity: 0, x: -24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                  >
                    <Card>
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-xl mb-2">{phase.name}</CardTitle>
                            <CardDescription>
                              {phase.startWeek}주차 ~ {phase.endWeek}주차 ({phase.duration})
                            </CardDescription>
                          </div>
                          <Badge variant="secondary">
                            Phase {index + 1}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <div>
                            <h4 className="font-semibold mb-2 flex items-center gap-2">
                              <CheckCircle2 className="w-4 h-4 text-primary" />
                              주요 작업
                            </h4>
                            <ul className="space-y-2">
                              {phase.tasks.map((task, taskIndex) => (
                                <li key={taskIndex} className="flex items-start gap-2 text-sm">
                                  <span className="text-muted-foreground mt-1">•</span>
                                  <span>{task}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <Separator />
                          <div>
                            <h4 className="font-semibold mb-2 flex items-center gap-2">
                              <Package className="w-4 h-4 text-primary" />
                              산출물
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {phase.deliverables.map((deliverable, delIndex) => (
                                <Badge key={delIndex} variant="outline">
                                  {deliverable}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </PlanSection>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mb-12"
          >
            <PlanSection title="주요 마일스톤">
              <div className="space-y-4">
                {milestones.map((milestone, index) => (
                  <motion.div
                    key={milestone.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: 0.5 + index * 0.05 }}
                  >
                    <Card>
                      <CardContent className="pt-6">
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0">
                            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                              <span className="text-lg font-bold text-primary">
                                {milestone.week}
                              </span>
                            </div>
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg mb-1">{milestone.name}</h3>
                            <p className="text-sm text-muted-foreground mb-2">
                              {formatDate(milestone.date)} (Week {milestone.week})
                            </p>
                            <p className="text-sm">{milestone.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </PlanSection>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <PlanSection title="기술 스택">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">프론트엔드</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {techStack.frontend.map((tech, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-sm">{tech.name}</p>
                            <p className="text-xs text-muted-foreground">{tech.description}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">백엔드</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {techStack.backend.map((tech, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-sm">{tech.name}</p>
                            <p className="text-xs text-muted-foreground">{tech.description}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">개발 도구</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {techStack.tools.map((tech, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-sm">{tech.name}</p>
                            <p className="text-xs text-muted-foreground">{tech.description}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </PlanSection>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
}
